/**
 * The days of the week as seven round toggles, Monday first: the days a
 * weekly schedule runs on (Feature 81 D3).
 */
import type { ReactElement } from 'react';

import { WEEK_ORDER } from '../../lib/schedule.js';

const LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'] as const;
const NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
] as const;
const SIZE = 28;

/** Seven toggles; `days` are the pressed ones, 0 for Sunday, kept in order. */
export function DayToggles({
  days,
  onChange,
}: {
  days: readonly number[];
  onChange: (days: number[]) => void;
}): ReactElement {
  const on = new Set(days);
  const flip = (day: number) =>
    onChange(on.has(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b));
  return (
    <span role="group" aria-label="Days" style={{ display: 'inline-flex', gap: 2 }}>
      {WEEK_ORDER.map((day) => {
        const pressed = on.has(day);
        return (
          <button
            key={day}
            type="button"
            aria-pressed={pressed}
            aria-label={NAMES[day]}
            onClick={() => flip(day)}
            style={{
              width: SIZE,
              height: SIZE,
              borderRadius: 'var(--radius-pill)',
              fontSize: 'var(--text-meta)',
              cursor: 'pointer',
              ...(pressed
                ? {
                    background: 'var(--fill-action)',
                    color: 'var(--text-inverse)',
                    border: '1px solid transparent',
                    fontWeight: 'var(--weight-semibold)',
                  }
                : {
                    background: 'transparent',
                    color: 'var(--text-muted)',
                    border: '1px solid var(--border-input)',
                  }),
            }}
          >
            {LETTERS[day]}
          </button>
        );
      })}
    </span>
  );
}
